<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import Text from '@uiKit/elements/Text.vue'
import AvailableBalanceCard from './AvailableBalanceCard.vue'
import QuickActionsCard from './QuickActionsCard.vue'
import DepositFundsCard from './DepositFundsCard.vue'
import DepositBankTransferStep from './DepositBankTransferStep.vue'
import DepositConfirmStep from './DepositConfirmStep.vue'
import WithdrawFundsCard from './WithdrawFundsCard.vue'
import WithdrawConfirmModal from './WithdrawConfirmModal.vue'
import WalletActivitySection from './WalletActivitySection.vue'
import { maskAccountNumber } from '@shared/utils'
import type {
  Transaction,
  TransactionFilters,
} from '../../transactions/types/transactionTypes'
import type { TransactionFilterOption } from '../../transactions/api/transactionParser'
import type { BankAccount, KycActionState } from '@modules/account'
import type { SlicealAccount } from '../types/fiatWalletTypes'
import { canTransactInWallet } from '../utils/walletAccessUtils'

type DepositStep = 1 | 2 | 3

interface SortOption {
  value: string
  label: string
}

interface Props {
  // Account access restricted (investorStatus = blocked): swaps Quick Actions
  // for a contact-support blocker and blocks the deposit/withdraw drawers.
  restricted?: boolean
  // KYC gate. Only 'verified' enables the deposit/withdraw drawers; 'in-review'
  // and 'action-required' swap Quick Actions for a notice and block the drawers.
  kycState?: KycActionState
  currency?: string
  balance?: string
  balanceAmount?: number
  defaultBankAccount?: BankAccount | null
  bankAccounts?: BankAccount[]
  slicealAccount?: SlicealAccount | null
  lastUpdated?: string
  // Activity feed (mirrors the standalone /wallet/transactions page that
  // used to live behind a "View All" link — now inline in the wallet card).
  activityTransactions?: Transaction[]
  activityLoading?: boolean
  activityLoadingMore?: boolean
  activityHasMore?: boolean
  filters: TransactionFilters
  typeOptions?: TransactionFilterOption[]
  statusOptions?: TransactionFilterOption[]
  sortOptions: SortOption[]
  activeFilterCount?: number
  downloading?: boolean
  downloadError?: string
  walletLoading?: boolean
  walletHasError?: boolean
  withdrawing?: boolean
  depositSubmitting?: boolean
  depositError?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  restricted: false,
  kycState: 'verified',
  currency: 'AED',
  balance: '',
  balanceAmount: 0,
  defaultBankAccount: null,
  bankAccounts: () => [],
  slicealAccount: null,
  lastUpdated: '',
  activityTransactions: () => [],
  activityLoading: false,
  activityLoadingMore: false,
  activityHasMore: false,
  typeOptions: () => [],
  statusOptions: () => [],
  activeFilterCount: 0,
  downloading: false,
  downloadError: '',
  walletLoading: false,
  walletHasError: false,
  withdrawing: false,
  depositSubmitting: false,
  depositError: null,
})

const emit = defineEmits<{
  (
    e: 'deposit-confirm',
    payload: { amount: number; referenceId: string; attachment: File },
  ): void
  (e: 'withdraw', payload: { amount: number; bankAccountId: string }): void
  (e: 'add-bank'): void
  (e: 'view-details', tx: Transaction): void
  (e: 'retry-wallet'): void
  (e: 'deposit-clear-error'): void
  (e: 'apply-filters', value: TransactionFilters): void
  (e: 'clear-filters'): void
  (e: 'apply-sort', value: string): void
  (e: 'load-more'): void
  (e: 'export-csv'): void
  (e: 'export-excel'): void
  (e: 'download-popover-toggle', open: boolean): void
}>()

const depositDrawerOpen = ref(false)
const depositStep = ref<DepositStep>(1)
const depositAmount = ref(0)

const withdrawDrawerOpen = ref(false)
const withdrawConfirmVisible = ref(false)
const pendingWithdrawal = ref<{ amount: number; bankAccountId: string } | null>(null)

const hasBankAccount = computed(() => props.bankAccounts.length > 0)
const hasActiveBank = computed(() =>
  props.bankAccounts.some((account) => account.accountStatus === 'verified'),
)

// Drawers open only when money-moving is unlocked — see canTransactInWallet
// (restricted account, KYC state, and bank verification all factor in).
const actionsBlocked = computed(
  () =>
    !canTransactInWallet({
      restricted: props.restricted,
      kycState: props.kycState,
      hasBankAccount: hasBankAccount.value,
      hasActiveBank: hasActiveBank.value,
    }),
)

const depositAmountFormatted = computed(() =>
  depositAmount.value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }),
)

const depositHeader = computed(() => {
  if (depositStep.value === 2) return 'Bank Transfer Details'
  if (depositStep.value === 3) return 'Confirm Transfer'
  return 'Add Funds'
})

watch(depositDrawerOpen, (open) => {
  if (!open) {
    depositStep.value = 1
    depositAmount.value = 0
    if (props.depositError) emit('deposit-clear-error')
  }
})

const onAddFunds = () => {
  if (actionsBlocked.value) return
  depositDrawerOpen.value = true
  depositStep.value = 1
}

const onWithdraw = () => {
  if (actionsBlocked.value) return
  withdrawDrawerOpen.value = true
}

const onDepositContinue = (amount: number) => {
  depositAmount.value = amount
  depositStep.value = 2
}

const onDepositBack = () => {
  depositStep.value = (depositStep.value - 1) as DepositStep
  if (props.depositError) emit('deposit-clear-error')
}

const onMadeTransfer = () => {
  depositStep.value = 3
}

const onDepositConfirm = (payload: { referenceId: string; attachment: File }) => {
  emit('deposit-confirm', {
    amount: depositAmount.value,
    referenceId: payload.referenceId,
    attachment: payload.attachment,
  })
}

const onDepositCancel = () => {
  depositDrawerOpen.value = false
}

const pendingWithdrawalBank = computed(() =>
  pendingWithdrawal.value
    ? (props.bankAccounts.find((account) => account.id === pendingWithdrawal.value?.bankAccountId) ??
      props.defaultBankAccount)
    : null,
)

const pendingWithdrawalRemaining = computed(() =>
  pendingWithdrawal.value
    ? Math.max(0, props.balanceAmount - pendingWithdrawal.value.amount)
    : 0,
)

// The card's "Confirm Withdrawal" no longer submits directly — it stages the
// request and opens a confirmation modal so the investor sees the consequence
// (amount, destination, balance after) before money moves.
const onWithdrawConfirm = (payload: { amount: number; bankAccountId: string }) => {
  pendingWithdrawal.value = payload
  withdrawConfirmVisible.value = true
}

const onWithdrawConfirmed = () => {
  if (!pendingWithdrawal.value) return
  emit('withdraw', pendingWithdrawal.value)
}

const onWithdrawConfirmCancel = () => {
  if (props.withdrawing) return
  withdrawConfirmVisible.value = false
  pendingWithdrawal.value = null
}

const onWithdrawCancel = () => {
  withdrawDrawerOpen.value = false
}

// Once the submission settles (success or error, handled by the container's
// result modals) tear down both the confirm modal and the drawer.
watch(
  () => props.withdrawing,
  (now, prev) => {
    if (prev && !now) {
      withdrawConfirmVisible.value = false
      withdrawDrawerOpen.value = false
      pendingWithdrawal.value = null
    }
  },
)

defineExpose({
  openDeposit: () => {
    if (actionsBlocked.value) return
    depositDrawerOpen.value = true
    depositStep.value = 1
  },
  closeDeposit: () => {
    depositDrawerOpen.value = false
  },
})
</script>

<template>
  <Box direction="col" gap="6" class="fiat-wallet-tab">
    <Box gap="6" class="fiat-wallet-tab__cards">
      <AvailableBalanceCard
        :currency="currency"
        :balance="balance"
        :last-updated="lastUpdated"
        :has-error="walletHasError"
        :loading="walletLoading"
        @retry="emit('retry-wallet')"
      />
      <QuickActionsCard
        :has-bank-account="hasBankAccount"
        :has-active-bank="hasActiveBank"
        :restricted="restricted"
        :kyc-state="kycState"
        @add-funds="onAddFunds"
        @withdraw="onWithdraw"
        @add-bank="emit('add-bank')"
      />
    </Box>

    <WalletActivitySection
      :transactions="activityTransactions"
      :loading="activityLoading"
      :loading-more="activityLoadingMore"
      :has-more="activityHasMore"
      :filters="filters"
      :type-options="typeOptions"
      :status-options="statusOptions"
      :sort-options="sortOptions"
      :active-filter-count="activeFilterCount"
      :downloading="downloading"
      :download-error="downloadError"
      @apply-filters="(value) => emit('apply-filters', value)"
      @clear-filters="() => emit('clear-filters')"
      @apply-sort="(value) => emit('apply-sort', value)"
      @load-more="() => emit('load-more')"
      @view-details="(tx) => emit('view-details', tx)"
      @export-csv="() => emit('export-csv')"
      @export-excel="() => emit('export-excel')"
      @download-popover-toggle="(open) => emit('download-popover-toggle', open)"
    />

    <Drawer v-model="depositDrawerOpen" position="right" width="480px">
      <template #header>
        <Text type="page-title" is="h2">{{ depositHeader }}</Text>
      </template>

      <DepositFundsCard
        v-if="depositStep === 1"
        @continue="onDepositContinue"
        @cancel="onDepositCancel"
      />

      <DepositBankTransferStep
        v-else-if="depositStep === 2"
        :amount="depositAmountFormatted"
        :sliceal-account="slicealAccount"
        @back="onDepositBack"
        @made-transfer="onMadeTransfer"
      />

      <DepositConfirmStep
        v-else
        :amount="depositAmountFormatted"
        :submit-error="depositError"
        :submitting="depositSubmitting"
        @back="onDepositBack"
        @confirm="onDepositConfirm"
      />
    </Drawer>

    <Drawer v-model="withdrawDrawerOpen" position="right" width="440px">
      <template #header>
        <Text type="page-title" is="h2">Withdraw Funds</Text>
      </template>
      <WithdrawFundsCard
        :balance="balanceAmount"
        :default-bank-account="defaultBankAccount"
        :accounts="bankAccounts"
        :loading="withdrawing"
        @confirm="onWithdrawConfirm"
        @cancel="onWithdrawCancel"
        @add-bank="emit('add-bank')"
      />
    </Drawer>

    <WithdrawConfirmModal
      v-if="pendingWithdrawal"
      :visible="withdrawConfirmVisible"
      :amount="pendingWithdrawal.amount"
      :bank-name="pendingWithdrawalBank?.bankName ?? '—'"
      :masked-account="maskAccountNumber(pendingWithdrawalBank?.accountNumber)"
      :remaining-balance="pendingWithdrawalRemaining"
      :loading="withdrawing"
      @confirm="onWithdrawConfirmed"
      @cancel="onWithdrawConfirmCancel"
    />
  </Box>
</template>

<style scoped>
.fiat-wallet-tab {
  height: 100%;
  min-height: 0;
}

.fiat-wallet-tab__cards {
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .fiat-wallet-tab__cards {
    flex-direction: column;
  }
}
</style>
